import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ts } from '../api';
import { Page, Section, Stat, Select, Table, Td, Pill, Empty, Loading, ErrorNote, useLoad, when } from '../ui';

export default function TSSupervisor() {
  const [days, setDays] = useState(1);
  const { data, loading, error, reload } = useLoad(() => ts.supervisor(days), [days]);
  const [note, setNote] = useState({ member_id: '', text: '' });

  const addNote = async (e) => {
    e.preventDefault();
    try { await ts.coaching(note); setNote({ member_id: note.member_id, text: '' }); reload(); toast.success('Coaching note saved'); } catch (err) { toast.error(err.message); }
  };

  if (loading && !data) return <Loading />;
  if (error) return <ErrorNote error={error} />;
  const o = data.overview;
  return (
    <Page title="Supervisor Center" actions={<Select value={days} onChange={(v) => setDays(Number(v))} options={[[1, 'Today'], [7, 'Last 7 days'], [30, 'Last 30 days']]} />}>
      {data.compliance_alerts.length > 0 && (
        <Section title="Alerts">
          <ul className="text-sm space-y-1" data-testid="ts-alerts">{data.compliance_alerts.map((a, i) => <li key={i} className="text-amber-700 dark:text-amber-400">{a.alert}</li>)}</ul>
        </Section>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Calls" value={o.calls.total} hint={`${o.calls.answered} answered`} />
        <Stat label="Missed" value={o.calls.missed} />
        <Stat label="Callbacks open" value={o.callbacks.open} hint={`${o.callbacks.overdue} overdue`} />
        <Stat label="AI waiting for approval" value={data.ai_pending_approval.length} />
      </div>
      <Section title="Team">
        <Table head={['Employee', 'Status', 'Calls', 'Outbound', 'Connected', 'Missed', 'Talk min', 'Callbacks done', 'Callbacks open']}>
          {o.staff.map(s => (
            <tr key={s.member_id}>
              <Td className="font-medium">{s.name}</Td>
              <Td><Pill tone={s.availability === 'online' ? 'green' : s.availability === 'busy' ? 'amber' : 'gray'}>{s.availability}</Pill></Td>
              <Td>{s.calls}</Td><Td>{s.outbound}</Td><Td>{s.connected}</Td><Td>{s.missed}</Td><Td>{s.talk_minutes}</Td><Td>{s.callbacks_done}</Td><Td>{s.callbacks_open}</Td>
            </tr>
          ))}
        </Table>
      </Section>
      <Section title="Live calls">
        {data.live_calls.length === 0 ? <Empty>No calls in progress.</Empty> : (
          <Table head={['Started', 'Employee', 'Customer', 'Direction', 'Status']}>
            {data.live_calls.map(c => <tr key={c.id}><Td>{when(c.created_at)}</Td><Td>{c.member_name || 'AI'}</Td><Td>{c.customer_number}</Td><Td>{c.direction}</Td><Td>{c.status}</Td></tr>)}
          </Table>
        )}
      </Section>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Section title="Call results">
          {Object.keys(data.dispositions).length === 0 ? <Empty>No results recorded.</Empty> : (
            <ul className="text-sm space-y-1">{Object.entries(data.dispositions).map(([k, v]) => <li key={k} className="flex justify-between"><span>{k.replace(/_/g, ' ')}</span><strong>{v}</strong></li>)}</ul>
          )}
        </Section>
        <Section title="Coaching notes">
          <form onSubmit={addNote} className="flex flex-wrap gap-2">
            <Select value={note.member_id} onChange={(m) => setNote({ ...note, member_id: m })} options={[['', 'Employee…'], ...o.staff.map(s => [s.member_id, s.name])]} />
            <Input value={note.text} onChange={e => setNote({ ...note, text: e.target.value })} placeholder="Note" className="flex-1 h-9 min-w-[160px]" />
            <Button size="sm" type="submit" disabled={!note.member_id || !note.text.trim()}>Add</Button>
          </form>
          <ul className="text-xs space-y-1 text-muted-foreground">{data.coaching_notes.map(n => <li key={n.id}><strong className="text-foreground">{n.member_name}</strong>: {n.text} — {n.by}, {when(n.created_at)}</li>)}</ul>
        </Section>
      </div>
      <Section title="Quality review (longest calls)">
        {data.quality_review_queue.length === 0 ? <Empty>No completed calls.</Empty> : (
          <Table head={['When', 'Employee', 'Customer', 'Length', 'Result', 'Summary']}>
            {data.quality_review_queue.map(c => <tr key={c.id}><Td>{when(c.created_at)}</Td><Td>{c.member_name || 'AI'}</Td><Td>{c.customer_number}</Td><Td>{c.duration_seconds}s</Td><Td>{c.disposition || '—'}</Td><Td className="text-xs max-w-xs">{c.summary || ''}{c.recording_url && <a className="text-primary ml-1" href={c.recording_url} target="_blank" rel="noreferrer">Recording</a>}</Td></tr>)}
          </Table>
        )}
      </Section>
      <Section title="Recent AI activity">
        <Table head={['When', 'Action', 'Status', 'By']}>
          {data.ai_recent.map(a => <tr key={a.id}><Td>{when(a.created_at)}</Td><Td>{a.tool}</Td><Td>{a.status}</Td><Td>{a.member_name}</Td></tr>)}
        </Table>
      </Section>
    </Page>
  );
}
